import {delay} from '@yourcoach/async-utils';

import type {Nullable} from '../utility-types';

import {logger} from '../logger';
import {base64ToUtf8, utf8ToBase64} from '../string';

const obfs = (
  str: Nullable<string>,
  key: number = 255,
  n: number = 254,
): string => {
  if (!str) {
    return '';
  }

  const base64Str = utf8ToBase64(str);
  const chars = base64Str.split('');

  for (let i = 0; i < chars.length; i += 1) {
    const c = chars[i]!.charCodeAt(0);

    if (c <= n) {
      chars[i] = String.fromCharCode((chars[i]!.charCodeAt(0) + key) % n);
    }
  }

  return chars.join('');
};

const defs = (
  str: Nullable<string>,
  key: number = 255,
  n: number = 254,
): string => {
  if (!str) {
    return '';
  }

  const chars = str.split('');

  for (let i = 0; i < chars.length; i += 1) {
    const c = chars[i]!.charCodeAt(0);

    if (c <= n) {
      chars[i] = String.fromCharCode((chars[i]!.charCodeAt(0) - key + n) % n);
    }
  }

  const base64Str = chars.join('');

  return base64ToUtf8(base64Str);
};

const isTestEnv = process.env.NODE_ENV === 'test';

let isStorageErrorLogged = false;

type StorageFn = keyof Pick<
  Storage,
  'clear' | 'getItem' | 'removeItem' | 'setItem'
>;

const getStorageFN = (fn: StorageFn) => {
  try {
    if (isTestEnv || typeof window === 'undefined') {
      return () => {};
    }

    return window.localStorage[fn].bind(window.localStorage);
  } catch (e) {
    // we do not want to log this error more than once per session
    if (!isStorageErrorLogged) {
      logger.error(
        new Error(`Error accessing storage FN: ${fn}. Message: ${e.message}`),
      );

      isStorageErrorLogged = true;
    }

    // eslint-disable-next-line no-console
    console.log(`ERROR accessing storage FN: ${fn}. Message: ${e.message}`);

    return () => {};
  }
};

/**
 * Bump version if you want to clear the storage.
 * For example, if we've changed the way we store data in the storage.
 */
const version = 'v2';
const storageKey = 'yc_sv';

export const storage = {
  clear: () =>
    new Promise<void>(resolve => {
      getStorageFN('clear')();
      storage.setItem(storageKey, version);
      resolve();
    }),
  getItem: (key: string) =>
    new Promise<string>(resolve => resolve(defs(getStorageFN('getItem')(key)))),
  onChange: (
    key: string,
    callback: (newValue: Nullable<string>, oldValue: Nullable<string>) => void,
  ) => {
    if (isTestEnv || typeof window === 'undefined') {
      return () => {};
    }

    const listener = (event: StorageEvent) => {
      if (event.key === key) {
        const oldValue = defs(event.oldValue);
        const newValue = defs(event.newValue);

        callback(newValue, oldValue);
      }
    };

    window.addEventListener('storage', listener);

    return () => {
      window.removeEventListener('storage', listener);
    };
  },
  removeItem: (key: string) =>
    new Promise<void>(resolve => resolve(getStorageFN('removeItem')(key))),
  setItem: (key: string, val: string) =>
    new Promise<void>(resolve =>
      resolve(getStorageFN('setItem')(key, obfs(val))),
    ),
};

async function init() {
  await delay(1000);

  const savedVersion = await storage.getItem(storageKey);

  if (savedVersion !== version) {
    await storage.clear();
    await storage.setItem(storageKey, version);
  }
}

init();
